import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'

export type { ColorScheme }
export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

const focusRingDark: SystemStyleObject = {
  outline: '2px solid',
  outlineColor: 'interactive.focus-ring',
  outlineOffset: '2px',
}

const focusRingLight: SystemStyleObject = {
  outline: '2px solid',
  outlineColor: 'light.interactive.focus-ring',
  outlineOffset: '2px',
}

function disabledState(bg: string, color: string): SystemStyleObject {
  return { bg, color, cursor: 'not-allowed', _hover: { bg } }
}

const sizeStyles: Record<ButtonSize, SystemStyleObject> = {
  // Table-row actions · dense UI
  sm: {
    h: '24px',
    px: '12px',
    textStyle: 'caption-medium',
    gap: '8px',
  },
  // Inline action rows · forms
  md: {
    h: '32px',
    px: '16px',
    textStyle: 'caption-medium',
    gap: '8px',
  },
  // Primary CTAs · login · checkout
  lg: {
    h: '40px',
    px: '16px',
    textStyle: 'body-large',
    gap: '8px',
  },
}

const lightPrimaryStyles: SystemStyleObject = {
  bg: 'light.interactive.primary',
  color: 'light.text.on-accent',
  borderWidth: '1px',
  borderColor: 'transparent',
  _hover: {
    bg: 'light.interactive.primary-hover',
  },
  _active: {
    bg: 'light.interactive.primary-active',
  },
  _disabled: disabledState(
    'light.interactive.primary-disabled-bg',
    'light.interactive.primary-disabled-text'
  ),
  _focusVisible: focusRingLight,
}

const lightSecondaryStyles: SystemStyleObject = {
  bg: 'transparent',
  color: 'light.text.secondary',
  borderWidth: '1px',
  borderColor: 'light.interactive.tertiary-border',
  _hover: {
    bg: 'light.interactive.tertiary-hover-bg',
  },
  _active: {
    bg: 'light.interactive.tertiary-hover-bg',
  },
  _disabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    _hover: { bg: 'transparent' },
  },
  _focusVisible: focusRingLight,
}

const lightTertiaryStyles: SystemStyleObject = {
  bg: 'light.interactive.tertiary-bg',
  color: 'light.interactive.tertiary-text',
  borderWidth: '1px',
  borderColor: 'light.interactive.tertiary-border',
  _hover: {
    bg: 'light.interactive.tertiary-hover-bg',
  },
  _active: {
    bg: 'light.interactive.tertiary-hover-bg',
  },
  _disabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    _hover: { bg: 'light.interactive.tertiary-bg' },
  },
  _focusVisible: focusRingLight,
}

const variantStyles: Record<ButtonVariant, SystemStyleObject> = {
  primary: {
    bg: 'interactive.primary',
    color: 'text.on-accent',
    borderWidth: '1px',
    borderColor: 'transparent',
    _hover: {
      bg: 'interactive.primary-hover',
    },
    _active: {
      bg: 'interactive.primary-active',
    },
    _disabled: disabledState(
      'interactive.primary-disabled-bg',
      'interactive.primary-disabled-text'
    ),
  },
  danger: {
    bg: 'interactive.danger',
    color: 'text.on-danger',
    borderWidth: '1px',
    borderColor: 'transparent',
    _hover: {
      bg: 'interactive.danger-hover',
    },
    _active: {
      bg: 'interactive.danger-active',
    },
    // Not the danger-disabled-* tokens: red.900 bg with red.800 text is
    // illegible. Fading the enabled colors instead reproduces the legacy v1
    // danger button's disabled treatment (whole button at 40% opacity),
    // which reads correctly on any surface.
    _disabled: {
      bg: 'interactive.danger',
      color: 'text.on-danger',
      opacity: 0.4,
      cursor: 'not-allowed',
      _hover: { bg: 'interactive.danger' },
    },
  },
  secondary: {
    bg: 'transparent',
    color: 'interactive.secondary-text',
    borderWidth: '1px',
    borderColor: 'interactive.secondary-border',
    _hover: {
      bg: 'interactive.secondary-hover-bg',
      borderColor: 'interactive.secondary-hover-border',
    },
    _active: {
      bg: 'interactive.secondary-active-bg',
      borderColor: 'interactive.secondary-active-border',
      color: 'interactive.secondary-active-text',
    },
    _disabled: {
      borderColor: 'interactive.secondary-disabled-border',
      color: 'interactive.secondary-disabled-text',
      cursor: 'not-allowed',
      _hover: {
        bg: 'transparent',
        borderColor: 'interactive.secondary-disabled-border',
      },
    },
  },
  tertiary: {
    bg: 'transparent',
    color: 'interactive.tertiary-text',
    borderWidth: '1px',
    borderColor: 'interactive.tertiary-border',
    _hover: {
      bg: 'interactive.tertiary-hover-bg',
      borderColor: 'border.glow',
      color: 'text.primary',
    },
    _active: {
      bg: 'interactive.tertiary-active-bg',
      borderColor: 'interactive.tertiary-active-border',
      color: 'interactive.tertiary-active-text',
    },
    _disabled: {
      borderColor: 'interactive.tertiary-disabled-border',
      color: 'interactive.tertiary-disabled-text',
      cursor: 'not-allowed',
      _hover: {
        bg: 'transparent',
        borderColor: 'interactive.tertiary-disabled-border',
      },
    },
  },
}

const lightDangerStyles: SystemStyleObject = {
  bg: 'light.interactive.danger',
  color: 'light.text.on-danger',
  borderWidth: '1px',
  borderColor: 'transparent',
  _hover: {
    bg: 'light.interactive.danger-hover',
  },
  _active: {
    bg: 'light.interactive.danger-active',
  },
  // Same legacy-style opacity fade as the dark danger variant — the
  // danger-disabled-* tokens put red text on a red background.
  _disabled: {
    bg: 'light.interactive.danger',
    color: 'light.text.on-danger',
    opacity: 0.4,
    cursor: 'not-allowed',
    _hover: { bg: 'light.interactive.danger' },
  },
}

export const buttonStyles = (
  variant: ButtonVariant,
  size: ButtonSize,
  borderRadius?: string | number,
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: borderRadius ?? '0',
  fontStyle: 'normal',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  userSelect: 'none',
  transition:
    'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
  _focusVisible: focusRingDark,
  ...sizeStyles[size],
  ...(colorScheme === 'light' && variant === 'primary'
    ? lightPrimaryStyles
    : colorScheme === 'light' && variant === 'secondary'
    ? lightSecondaryStyles
    : colorScheme === 'light' && variant === 'tertiary'
    ? lightTertiaryStyles
    : colorScheme === 'light' && variant === 'danger'
    ? lightDangerStyles
    : variantStyles[variant]),
})
